import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Stethoscope, FileText, IndianRupee, ChevronRight, Download, CheckCircle, Clock } from "lucide-react";

function pretty(dtIso) {
    try {
        return new Date(dtIso).toLocaleString("en-IN", {
            dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata",
        });
    } catch { return dtIso; }
}

const STATUS_STYLES = {
    pending_payment: { bg: "bg-amber-100 text-amber-800", label: "Awaiting payment" },
    payment_received: { bg: "bg-emerald-100 text-emerald-800", label: "Paid · Review" },
    in_review: { bg: "bg-sky-100 text-sky-800", label: "In review" },
    completed: { bg: "bg-slate-100 text-slate-700", label: "Completed" },
    refunded: { bg: "bg-rose-100 text-rose-800", label: "Refunded" },
    cancelled: { bg: "bg-rose-100 text-rose-800", label: "Cancelled" },
};

export default function DoctorSecondOpinions() {
    const [doctor, setDoctor] = useState(null);
    const [settings, setSettings] = useState({
        enabled: false, price_inr: 1500, response_sla_hours: 48, intro_text: "",
    });
    const [cases, setCases] = useState([]);
    const [selected, setSelected] = useState(null);  // full case detail (with files)
    const [response, setResponse] = useState("");
    const [saving, setSaving] = useState(false);
    const [sendingResp, setSendingResp] = useState(false);

    async function loadAll() {
        try {
            const [d, s, c] = await Promise.all([
                api.get("/me/doctor"),
                api.get("/me/second-opinion/settings"),
                api.get("/me/second-opinion/cases"),
            ]);
            setDoctor(d.data);
            setSettings({
                enabled: !!s.data?.enabled,
                price_inr: s.data?.price_inr ?? 1500,
                response_sla_hours: s.data?.response_sla_hours ?? 48,
                intro_text: s.data?.intro_text ?? "",
            });
            setCases(c.data || []);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => { loadAll(); }, []);

    async function saveSettings() {
        setSaving(true);
        try {
            await api.put("/me/second-opinion/settings", {
                enabled: !!settings.enabled,
                price_inr: Number(settings.price_inr),
                response_sla_hours: Number(settings.response_sla_hours),
                intro_text: settings.intro_text?.trim() || "",
            });
            toast.success("Settings saved");
            loadAll();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    }

    async function openCase(c) {
        try {
            const r = await api.get(`/me/second-opinion/cases/${c.id}`);
            setSelected(r.data);
            setResponse(r.data?.response_text || "");
        } catch (e) {
            toast.error(errMsg(e, "Could not open"));
        }
    }

    async function sendResponse(markComplete) {
        if (!response || response.trim().length < 10) {
            toast.error("Response must be at least 10 characters");
            return;
        }
        setSendingResp(true);
        try {
            await api.post(`/me/second-opinion/cases/${selected.id}/respond`, {
                response_text: response.trim(),
                mark_complete: !!markComplete,
            });
            toast.success(markComplete ? "Response sent to patient" : "Draft saved");
            setSelected(null); setResponse("");
            loadAll();
        } catch (e) {
            toast.error(errMsg(e, "Send failed"));
        } finally {
            setSendingResp(false);
        }
    }

    async function downloadFile(url, filename) {
        try {
            const r = await api.get(url.replace("/api/v1", ""), { responseType: "blob" });
            const u = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement("a");
            a.href = u; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(u);
        } catch (e) {
            toast.error(errMsg(e, "Download failed"));
        }
    }

    const inboxCount = cases.filter(c => c.status === "payment_received" || c.status === "in_review").length;

    return (
        <DoctorShell doctor={doctor}>
            <div className="space-y-6" data-testid="doctor-second-opinions">
                <div>
                    <h1 className="text-3xl font-bold text-[#123B5D]">Second Opinion service</h1>
                    <p className="text-[#17212B]/60 mt-1 text-sm">
                        Opt-in to accept paid remote second opinions on uploaded reports. Patients submit on your
                        public profile — payment goes through the platform; the case lands in your inbox below.
                    </p>
                </div>

                {/* Settings */}
                <Card data-testid="so-settings-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#123B5D]">
                            <Stethoscope size={18} /> Service settings
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-[#123B5D]">Accept second opinion requests</div>
                                <div className="text-xs text-[#17212B]/60">A Second Opinion section appears on your public profile when enabled.</div>
                            </div>
                            <Switch data-testid="so-enabled-toggle" checked={!!settings.enabled}
                                onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-[#17212B]/70 font-semibold flex items-center gap-1">
                                    <IndianRupee size={12} /> Fee per case (₹500 – ₹5,000)
                                </label>
                                <Input data-testid="so-price-input" type="number" min={500} max={5000} step={100}
                                    value={settings.price_inr}
                                    onChange={(e) => setSettings({ ...settings, price_inr: Number(e.target.value) })} />
                            </div>
                            <div>
                                <label className="text-xs text-[#17212B]/70 font-semibold flex items-center gap-1">
                                    <Clock size={12} /> Promised response time (hours)
                                </label>
                                <Input data-testid="so-sla-input" type="number" min={1} max={168}
                                    value={settings.response_sla_hours}
                                    onChange={(e) => setSettings({ ...settings, response_sla_hours: Number(e.target.value) })} />
                                <div className="text-[11px] text-[#64748B] mt-1" data-testid="so-sla-help">
                                    Enter the number of <strong>hours</strong> you commit to responding within.
                                    Common choices: 24 (1 day) · 48 (2 days) · 72 (3 days) · 168 (1 week).
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-[#17212B]/70 font-semibold">Intro shown on your public form (optional)</label>
                            <Textarea data-testid="so-intro-input" rows={2} maxLength={1200}
                                value={settings.intro_text}
                                onChange={(e) => setSettings({ ...settings, intro_text: e.target.value })}
                                placeholder="E.g. 'I offer written second opinions on dermatology biopsies and derm imaging. Please share clear photos and your prior consultation notes.'" />
                        </div>
                        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-[12px] text-[#17212B]/80 leading-relaxed">
                            <strong className="text-[#123B5D]">Medically-safe disclaimer baked into every form, confirmation and response:</strong><br />
                            "This is a professional opinion service and does not constitute emergency care or a doctor-patient treatment relationship. For emergencies, call your local emergency number."
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={saveSettings} disabled={saving} data-testid="so-save-settings">
                                {saving ? "Saving…" : "Save settings"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Inbox */}
                <Card data-testid="so-inbox-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#123B5D]">
                            <FileText size={18} /> Cases inbox ({inboxCount} awaiting)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {cases.length === 0 && (
                            <div className="text-sm text-[#17212B]/60 py-6 text-center" data-testid="so-cases-empty">
                                No Second Opinion cases yet. Enable the service to accept patient requests.
                            </div>
                        )}
                        <div className="space-y-2">
                            {cases.map((c) => {
                                const st = STATUS_STYLES[c.status] || { bg: "bg-slate-100 text-slate-700", label: c.status };
                                return (
                                    <button key={c.id} onClick={() => openCase(c)}
                                        className="w-full text-left flex items-center justify-between gap-3 border rounded-md p-3 hover:bg-slate-50 transition"
                                        data-testid={`so-case-row-${c.id}`}>
                                        <div>
                                            <div className="font-semibold text-[#123B5D] text-sm">{c.patient_name}</div>
                                            <div className="text-xs text-[#17212B]/60">
                                                {pretty(c.created_at)} · {c.patient_phone} · ₹{c.price_inr} · {c.files_count} file{c.files_count === 1 ? "" : "s"}
                                            </div>
                                            <div className="text-xs mt-1 text-[#17212B]/70 line-clamp-2">{c.description}</div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${st.bg}`}>{st.label}</span>
                                            <ChevronRight size={14} className="text-[#17212B]/40" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Detail drawer */}
                {selected && (
                    <Card data-testid="so-detail-card" className="border-[#0F766E] border-2">
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between gap-2 text-[#123B5D]">
                                <span className="flex items-center gap-2"><FileText size={18} /> Case: {selected.patient_name}</span>
                                <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="so-detail-close">Close</Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div><strong>Phone:</strong> {selected.patient_phone}</div>
                                <div><strong>Email:</strong> {selected.patient_email || <span className="text-[#17212B]/50">—</span>}</div>
                                <div><strong>Fee paid:</strong> ₹{selected.price_inr}</div>
                                <div><strong>Status:</strong> {STATUS_STYLES[selected.status]?.label}</div>
                            </div>
                            <div className="rounded-md bg-slate-50 border p-3">
                                <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold mb-1">Patient's concern</div>
                                <div className="text-sm text-[#17212B] whitespace-pre-wrap">{selected.description}</div>
                            </div>
                            {selected.files?.length > 0 && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold mb-2">Uploaded reports</div>
                                    <div className="flex flex-wrap gap-2">
                                        {selected.files.map((f) => (
                                            <Button key={f.id} variant="outline" size="sm"
                                                onClick={() => downloadFile(f.download_url, f.filename)}
                                                data-testid={`so-file-dl-${f.id}`}
                                                className="text-xs">
                                                <Download size={12} className="mr-1" /> {f.filename}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selected.status === "completed" ? (
                                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
                                    <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-1 flex items-center gap-1">
                                        <CheckCircle size={12} /> Response sent {selected.responded_at && `· ${pretty(selected.responded_at)}`}
                                    </div>
                                    <div className="text-sm text-[#17212B] whitespace-pre-wrap">{selected.response_text}</div>
                                </div>
                            ) : (
                                <div>
                                    <label className="text-xs text-[#17212B]/70 font-semibold">Your response (sent to patient by email + WhatsApp)</label>
                                    <Textarea data-testid="so-response-input" rows={8} maxLength={20000}
                                        value={response}
                                        onChange={(e) => setResponse(e.target.value)}
                                        placeholder="Dear patient, based on the reports you shared..."
                                        className="font-mono text-sm" />
                                    <div className="text-[11px] text-[#17212B]/50 mt-1">
                                        {response.length}/20000 characters. The response is for professional information only; it is not a treatment plan or an emergency instruction.
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-3">
                                        <Button variant="outline" onClick={() => sendResponse(false)}
                                            disabled={sendingResp} data-testid="so-save-draft">
                                            Save draft
                                        </Button>
                                        <Button onClick={() => sendResponse(true)}
                                            disabled={sendingResp} data-testid="so-send-complete"
                                            className="bg-[#0F766E] hover:bg-[#0b5a52]">
                                            {sendingResp ? "Sending…" : "Send & mark complete"}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </DoctorShell>
    );
}
